import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type } from "arktype";
import { ClipboardPaste, RefreshCw } from "lucide-react";

import { orpc } from "@/client";
import { DraftsList } from "@/components/manuais/drafts-list";
import { Button } from "@/components/ui/button";
import type { ManualFilter } from "@/components/manuais/manual-list";
import { ManualList } from "@/components/manuais/manual-list";
import { ArchivedManuals } from "@/components/medico/archived-manuals";
import { ExamQueue } from "@/components/medico/exam-queue";
import { ExamWithoutOwner } from "@/components/medico/exam-without-owner";
import { IgnoredSection } from "@/components/medico/ignored-section";
import { JobErrors } from "@/components/medico/job-errors";
import { GateBanner } from "@/components/medico/orphan-relink";
import { AvulsaModal } from "@/components/medico/paste-transcript";
import { PipelineQueue } from "@/components/medico/pipeline-queue";
import { WorkQueue } from "@/components/medico/work-queue";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonRows } from "@/components/ui/skeleton";
import { useManualList } from "@/hooks/use-manuals";
import { useMedicoSync } from "@/hooks/use-medico";
import { usePageVisible } from "@/hooks/use-page-visible";
import { format } from "@/lib/format";
import { ClinicaLabels } from "@/lib/labels/clinica";
import { MedicoLabels } from "@/lib/labels/medico";
import { PollCadence } from "@/lib/poll-cadence";
import { fallback, pageParam } from "@/lib/search-params";
import { cn } from "@/lib/utils";

import { PendencySummary } from "./-components/pendency-summary";
import { TermsRail } from "./-components/terms-rail";

const MANUAL_FILTERS = ["all", "draft", "delivered", "not_opened", "reread", "revoked"] as const;

const searchSchema = type({
	page: pageParam,
	examPage: pageParam,
	manualPage: pageParam,
	manualFilter: fallback(type.enumerated(...MANUAL_FILTERS), "all"),
});

export const Route = createFileRoute("/_app/clinica/")({
	validateSearch: searchSchema,
	component: ClinicaPage,
});

function ClinicaPage() {
	const { page, examPage, manualPage, manualFilter } = Route.useSearch();
	const navigate = Route.useNavigate();
	const visible = usePageVisible();
	const { data } = useQuery(
		orpc.medico.fila.queryOptions({
			refetchInterval: () => PollCadence.pipeline({ visible }),
		}),
	);
	const pendencias = useQuery(orpc.clinica.pendencias.queryOptions());
	const sync = useMedicoSync();
	const manuals = useManualList({ filter: manualFilter, page: manualPage });

	const [pasting, setPasting] = useState(false);

	const summary =
		pendencias.data === undefined ? undefined : ClinicaLabels.summary(pendencias.data.length);

	return (
		<PageContainer>
			<PageHeader
				title={ClinicaLabels.pageTitle}
				description={summary ?? ClinicaLabels.pageDescription}
				actions={
					<Button type="button" variant="outline" size="sm" onClick={() => setPasting(true)}>
						<ClipboardPaste />
						{MedicoLabels.paste.trigger}
					</Button>
				}
			/>

			{!data && <SkeletonRows rows={6} />}

			{!!data && (
				<div className="flex flex-col gap-4">
					<PendencySummary items={pendencias.data} />

					<GateBanner orphans={data.orphansList} awaiting={data.awaitingList} />

					<div className="flex flex-col gap-6 lg:flex-row">
						<div className="flex min-w-0 flex-1 flex-col gap-4">
							<div id="fila-transcricoes" className="scroll-mt-4">
								<WorkQueue
									page={page}
									onPage={(next) => navigate({ search: (prev) => ({ ...prev, page: next }) })}
								/>
							</div>

							<div id="fila-exames" className="scroll-mt-4">
								<ExamQueue
									page={examPage}
									onPage={(next) => navigate({ search: (prev) => ({ ...prev, examPage: next }) })}
								/>
							</div>

							<div id="fila-exames-sem-dona" className="scroll-mt-4">
								<ExamWithoutOwner />
							</div>

							<div id="fila-processamento" className="scroll-mt-4">
								<PipelineQueue />
							</div>

							<ManualList
								items={manuals.data?.items}
								pages={manuals.data?.pages ?? 1}
								page={manualPage}
								filter={manualFilter}
								onFilter={(next: ManualFilter) =>
									navigate({ search: (prev) => ({ ...prev, manualFilter: next, manualPage: 1 }) })
								}
								onPage={(next) => navigate({ search: (prev) => ({ ...prev, manualPage: next }) })}
								isLoading={manuals.isLoading}
								isError={manuals.isError}
							/>
						</div>

						<aside className="flex w-full shrink-0 flex-col gap-4 lg:w-80">
							<SourceCard
								live={data.live}
								syncedAt={data.syncedAt}
								refreshing={sync.isPending}
								onRefresh={() => sync.mutate({ force: true })}
							/>

							<div id="rail-rascunhos" className="scroll-mt-4">
								<DraftsList />
							</div>

							<div id="rail-termos" className="scroll-mt-4">
								<TermsRail />
							</div>

							<JobErrors jobs={data.jobsError} />

							<IgnoredSection transcripts={data.ignored} />

							<ArchivedManuals manuals={data.archivedManuals} />
						</aside>
					</div>
				</div>
			)}

			{pasting && <AvulsaModal onClose={() => setPasting(false)} />}
		</PageContainer>
	);
}

function SourceCard({
	live,
	syncedAt,
	refreshing,
	onRefresh,
}: {
	live: boolean;
	syncedAt: string | null;
	refreshing: boolean;
	onRefresh: () => void;
}) {
	const seal = MedicoLabels.firefliesSeal;
	const sub = live
		? syncedAt !== null && `${seal.syncSubPrefix} ${format.dateTime(syncedAt)}`
		: seal.downSub;

	return (
		<section className="surface-panel flex items-center gap-2.5 px-4 py-3">
			<span
				className={cn(
					"size-2 shrink-0 rounded-full",
					live ? "bg-success ring-4 ring-success-soft" : "bg-warning ring-4 ring-warning-soft",
				)}
			/>

			<div className="min-w-0 flex-1 leading-tight">
				<p className="truncate text-xs font-semibold text-foreground">
					{live && seal.liveLead}
					{!live && seal.downLead}
				</p>

				{!!sub && <p className="truncate text-xs text-muted-foreground">{sub}</p>}
			</div>

			<button
				type="button"
				disabled={refreshing}
				onClick={onRefresh}
				className={cn(
					"control-button button-secondary min-h-11 px-3 py-2 text-xs md:min-h-9",
					"bg-primary-soft text-primary hover:bg-primary-soft/70",
				)}
			>
				<RefreshCw className={cn("size-3.5", refreshing && "animate-spin")} />
				{seal.refresh}
			</button>
		</section>
	);
}
